import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Trophy, Medal, Award, User as UserIcon, Save, EyeOff, TrendingUp, Users } from "lucide-react";

/**
 * Anonymous monthly leaderboard.
 * User must set a pseudo (opt-in) to appear.
 * Ranks by best-account P&L% for the calendar month.
 */
export default function Leaderboard() {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState("");
  const [pseudo, setPseudo] = useState("");
  const [optedIn, setOptedIn] = useState(false);
  const [editPseudo, setEditPseudo] = useState("");
  const [saving, setSaving] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem("auth_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadBoard = async () => {
    try {
      const r = await axios.get(`${API}/leaderboard/monthly`);
      setItems(r.data.items || []);
      setMonth(r.data.month || "");
    } catch {}
  };

  useEffect(() => {
    loadBoard();
    (async () => {
      try {
        const r = await axios.get(`${API}/users/pseudo`, { headers: authHeader() });
        setPseudo(r.data.pseudo || "");
        setOptedIn(!!r.data.opted_in);
        setEditPseudo(r.data.pseudo || "");
      } catch {}
    })();
  }, []);

  const savePseudo = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/users/pseudo`, { pseudo: editPseudo.trim() }, { headers: authHeader() });
      setPseudo(r.data.pseudo);
      setOptedIn(true);
      toast.success(`Pseudo « ${r.data.pseudo} » enregistré — vous êtes maintenant sur le leaderboard`);
      loadBoard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const optOut = async () => {
    try {
      await axios.delete(`${API}/users/pseudo`, { headers: authHeader() });
      setOptedIn(false);
      toast.success("Vous n'apparaissez plus sur le leaderboard");
      loadBoard();
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <div className="p-6 max-w-4xl" data-testid="leaderboard-page">
      <div className="flex items-center gap-3 mb-2">
        <Trophy size={22} className="text-yellow-400" />
        <h1 className="font-heading font-bold text-2xl">Leaderboard mensuel</h1>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/5 text-white/60 border border-white/10">
          {month}
        </span>
      </div>
      <p className="text-white/60 text-sm mb-6 max-w-3xl">
        Classement anonyme des meilleurs P&L% mensuels remontés par les Bridge EAs des membres ANATRADEX.
        Un pseudo obligatoire — <strong className="text-white">votre email et vos comptes réels restent privés</strong>.
      </p>

      {/* Opt-in form */}
      <div className="panel p-5 mb-6" data-testid="leaderboard-optin">
        <div className="flex items-center gap-2 mb-3">
          <UserIcon size={16} className="text-accent-blue" />
          <div className="font-heading font-semibold text-sm">
            {optedIn ? "Vous êtes inscrit(e)" : "Rejoindre le classement"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input type="text" className="input flex-1 min-w-[200px] font-mono" placeholder="Votre pseudo (3-20 caractères)"
                 maxLength={20} value={editPseudo} onChange={(e) => setEditPseudo(e.target.value)}
                 data-testid="leaderboard-pseudo-input" />
          <button className="btn btn-primary" onClick={savePseudo} disabled={saving || editPseudo.length < 3}
                  data-testid="leaderboard-pseudo-save">
            <Save size={13} /> {optedIn ? "Mettre à jour" : "Rejoindre"}
          </button>
          {optedIn && (
            <button className="btn btn-secondary" onClick={optOut} data-testid="leaderboard-optout">
              <EyeOff size={13} /> Me retirer
            </button>
          )}
        </div>
      </div>

      {/* Board */}
      <div className="panel p-0 overflow-hidden" data-testid="leaderboard-table">
        {items.length === 0 ? (
          <div className="text-center py-12 text-white/40">
            <Users size={32} className="mx-auto mb-3 text-white/20" />
            <div className="font-heading font-semibold mb-1">Aucun trader classé ce mois-ci</div>
            <div className="text-xs">Soyez le premier à connecter un compte et à choisir un pseudo !</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs text-white/50 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left w-16">Rang</th>
                <th className="px-4 py-2 text-left">Trader</th>
                <th className="px-4 py-2 text-left">Prop firm</th>
                <th className="px-4 py-2 text-right">P&L%</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.rank} className={`border-t border-white/5 ${r.pseudo === pseudo ? "bg-accent-blue/10" : ""}`}
                    data-testid={`leaderboard-row-${r.rank}`}>
                  <td className="px-4 py-3">
                    {r.rank === 1 ? <Trophy size={18} className="text-yellow-400" /> :
                     r.rank === 2 ? <Medal size={18} className="text-slate-300" /> :
                     r.rank === 3 ? <Award size={18} className="text-orange-400" /> :
                     <span className="font-mono text-white/50">#{r.rank}</span>}
                  </td>
                  <td className="px-4 py-3 font-heading font-semibold">
                    {r.pseudo}
                    {r.pseudo === pseudo && <span className="text-[10px] text-accent-blue ml-2">(vous)</span>}
                  </td>
                  <td className="px-4 py-3 text-white/60 text-xs uppercase">{r.prop_firm} · {r.broker}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${r.pnl_pct >= 0 ? "text-accent-green" : "text-red-400"}`}>
                    <span className="inline-flex items-center gap-1">
                      <TrendingUp size={11} /> {r.pnl_pct >= 0 ? "+" : ""}{r.pnl_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[10px] text-white/30 mt-3 leading-relaxed">
        Les P&L% affichés sont basés sur l'équité remontée par le Bridge EA. Aucune donnée personnelle n'est exposée.
        Vous pouvez vous retirer à tout moment via le bouton « Me retirer ».
      </p>
    </div>
  );
}
